import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {OutsideClickBehavior} from '../behaviors/outside-click-behavior';

class KeliDropdownContainer extends OutsideClickBehavior(PolymerElement) {
  static get template() {
    return html`
<style>
  :host {
    display: none;
    box-sizing: border-box;
    position: absolute;
    background-color: var(--keli-background-color);
    box-shadow: var(--keli-shadow-z1);
    border: var(--keli-container-border);
    z-index: var(--keli-zindex-input-field-dropdown);
    top: var(--keli-dropdown-container-top);
    right: var(--keli-dropdown-container-right);
    left: var(--keli-dropdown-container-left);
    min-width: var(--keli-dropdown-container-min-width);
    max-width: var(--keli-dropdown-container-max-width);
    max-height: var(--keli-dropdown-container-max-height);
    overflow-y: auto;
  }

  :host(.visible) {
    display: inline-block;
  }
</style>
`;
  }

  static get is() {
    return 'keli-dropdown-container';
  }

  static get properties() {
    return {
      element: Object,
      panel: Object,
      positionElement: Object,
      toggleElement: Object,
      sizeToElement: Boolean
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.element) {
      this.element.dispatchEvent(new CustomEvent('registerOverlayContainerWithPanel', {
        bubbles: true,
        composed: true,
        detail: {container: this}
      }));
    }

    this.addEventListener('panelDragMove', this.positionFromEvent);
    this.addEventListener('resizeElement', this.positionFromEvent);
    this.addEventListener('scrollElement', this.positionFromEvent);
    this.addEventListener('menuItemActivated', this.hideFromActivation);
    this.addEventListener('outside-click', this.hide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('panelDragMove', this.positionFromEvent);
    this.removeEventListener('resizeElement', this.positionFromEvent);
    this.removeEventListener('scrollElement', this.positionFromEvent);
    this.removeEventListener('menuItemActivated', this.hideFromActivation);
    this.removeEventListener('outside-click', this.hide);

    this.element = null;
    this.positionElement = null;
    this.toggleElement = null;
    this.panel = null;
  }

  queryContent(selector) {
    return this.shadowRoot.querySelectorAll(selector);
  }

  addContentElement(element) {
    this.shadowRoot.appendChild(element);
  }

  removeContent(selector) {
    const items = this.shadowRoot.querySelectorAll(selector);
    items.forEach(item => {
      item.remove()
    });
  }

  replaceContentElements(nodeList) {
    const children = Array.from(this.shadowRoot.children);
    children.shift(); // Remove STYLE element

    for (const [index, node] of nodeList.entries()) {
      if (children[index] === node) {
        continue;
      }

      if (children[index]) {
        children[index].insertAdjacentElement('beforebegin', node);
      } else {
        this.shadowRoot.appendChild(node);
      }
    }
  }

  applyCustomStyles(styles) {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    this.shadowRoot.appendChild(styleElement);
  }

  show() {
    if (this.visible()) {
      return;
    }
    this.position(); // Position twice on show to prevent position flickering.
    this.classList.add('visible');
    afterNextRender(this, () => {
      this.position();
    });
    if (this.element && typeof this.element.dropdownElementVisibleChanged === 'function') {
      this.element.dropdownElementVisibleChanged(true);
    }

    this.outsideClickElements = [this.toggleElement, this.element];
    this.outsideClickListening = true;
  }

  hide() {
    if (!this.visible()) {
      return;
    }
    this.classList.remove('visible');
    if (this.element && typeof this.element.dropdownElementVisibleChanged === 'function') {
      this.element.dropdownElementVisibleChanged(false);
    }

    this.outsideClickListening = false;
  }

  hideFromActivation() {
    if (!this.visible()) {
      return;
    }
    this.hide();
    if (this.panel) {
      this.panel.dispatchEvent(new CustomEvent('globalFocusReleased', {bubbles: true, composed: true}));
    }
  }

  visible() {
    return this.classList.contains('visible');
  }

  positionFromEvent(event) {
    if (!this.visible()) {
      return;
    }
    if (this.panel && event.detail.element && this.panel !== event.detail.element) {
      return;
    }
    this.position();
  }

  position() {
    if (!this.isConnected) return;

    const elementSize = (this.positionElement || this.element).getBoundingClientRect();

    const currentLeft = elementSize.left;
    let currentTop = elementSize.top;

    if (document.body.scrollTop) {
      currentTop += document.body.scrollTop;
    }

    const update = {
      top: Math.floor(currentTop + elementSize.height),
      right: Math.floor(document.body.clientWidth - currentLeft - elementSize.width)
    };

    if (this.sizeToElement) {
      update.width = Math.floor(elementSize.width);
      update.maxWidth = Math.floor(elementSize.width * 2);
    }

    // See if we need to position above the field.
    if ((update.top + this.scrollHeight) > document.body.offsetHeight) {
      let aboveTop = Math.ceil(elementSize.top - this.scrollHeight);
      if (aboveTop > 0) {
        update.top = aboveTop;
      } else {
        update.maxHeight = document.body.offsetHeight - update.top - 5;
      }
    }

    // See if we need to position from the left of the field.
    if ((document.body.offsetWidth - (update.right + this.offsetWidth)) < 0) {
      update.right = undefined;
      update.left = elementSize.left;
    }

    this.updateStyles({
      '--keli-dropdown-container-top': (update.top !== undefined ? `${update.top}px` : undefined),
      '--keli-dropdown-container-right': (update.right !== undefined ? `${update.right}px` : undefined),
      '--keli-dropdown-container-left': (update.left !== undefined ? `${update.left}px` : undefined),
      '--keli-dropdown-container-min-width': (update.width !== undefined ? `${update.width}px` : undefined),
      '--keli-dropdown-container-max-width': (update.maxWidth !== undefined ? `${update.maxWidth}px` : undefined),
      '--keli-dropdown-container-max-height': (update.maxHeight !== undefined ? `${update.maxHeight}px` : undefined),
    });
  }
}

customElements.define(KeliDropdownContainer.is, KeliDropdownContainer);
export {KeliDropdownContainer};
